import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getBbpsReport, downloadBbpsReportCsv, downloadBbpsReportPdf } from '../../lib/api';

const KIND_META = {
  api_balance:  { title: 'API Balance Ledger',    icon: '🏦', color: 'emerald' },
  wallets:      { title: 'Wallet Ledger',         icon: '👛', color: 'purple' },
  volume:       { title: 'Transaction Volume',    icon: '💸', color: 'blue' },
  success_rate: { title: 'Success Rate Analysis', icon: '🎯', color: 'orange' },
};

// Preset date ranges
const PRESETS = [
  { key: 'today', label: 'Today', offset: 0 },
  { key: '7d', label: 'Last 7 days', offset: 7 },
  { key: '30d', label: 'Last 30 days', offset: 30 },
  { key: '90d', label: 'Last 90 days', offset: 90 },
  { key: 'all', label: 'All time', offset: null },
];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function applyPreset(preset) {
  if (preset.offset === null) return { start: '', end: '' };
  const end = new Date();
  if (preset.offset === 0) return { start: ymd(end), end: ymd(end) };
  const start = new Date();
  start.setDate(end.getDate() - preset.offset);
  return { start: ymd(start), end: ymd(end) };
}

export default function BbpsReportModal({ kind, onClose }) {
  const meta = KIND_META[kind] || { title: kind, icon: '📊', color: 'purple' };
  const [{ start, end }, setRange] = useState(applyPreset(PRESETS[2])); // last 30d default
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [preset, setPreset] = useState('30d');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const res = await getBbpsReport(kind, params);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Report load fail');
    } finally { setLoading(false); }
  }, [kind, start, end]);

  useEffect(() => { load(); }, [load]);

  const applyPresetKey = (k) => {
    setPreset(k);
    const p = PRESETS.find(x => x.key === k);
    if (p) setRange(applyPreset(p));
  };

  const downloadBlob = (blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    try {
      setBusy('csv');
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const res = await downloadBbpsReportCsv(kind, params);
      downloadBlob(res.data, `bbps_${kind}_${start || 'all'}_${end || 'today'}.csv`);
      toast.success('CSV downloaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'CSV export fail'); }
    finally { setBusy(''); }
  };

  const exportPdf = async () => {
    try {
      setBusy('pdf');
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const res = await downloadBbpsReportPdf(kind, params);
      downloadBlob(res.data, `bbps_${kind}_${start || 'all'}_${end || 'today'}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'PDF export fail'); }
    finally { setBusy(''); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4" data-testid={`bbps-report-modal-${kind}`}>
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[95vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-3xl">{meta.icon}</span>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{meta.title}</h2>
              <p className="text-xs text-gray-500">{start || 'All'} → {end || 'today'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-2xl px-2" data-testid="bbps-report-close">✕</button>
        </div>

        {/* Filter bar */}
        <div className="p-3 border-b bg-slate-50 flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 flex-wrap">
            {PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => applyPresetKey(p.key)}
                data-testid={`preset-${p.key}`}
                className={`text-xs px-2 py-1 rounded ${preset === p.key ? 'bg-purple-600 text-white' : 'bg-white border hover:bg-slate-100'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <span className="text-gray-300">|</span>
          <input
            type="date"
            value={start}
            onChange={(e) => { setRange(r => ({ ...r, start: e.target.value })); setPreset(''); }}
            className="border rounded px-2 py-1 text-xs"
            data-testid="report-start-date"
          />
          <span className="text-gray-500 text-xs">to</span>
          <input
            type="date"
            value={end}
            onChange={(e) => { setRange(r => ({ ...r, end: e.target.value })); setPreset(''); }}
            className="border rounded px-2 py-1 text-xs"
            data-testid="report-end-date"
          />
          <button onClick={load} disabled={loading} className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1 rounded">
            {loading ? '...' : 'Refresh'}
          </button>
          <div className="ml-auto flex gap-1">
            <button
              onClick={exportCsv}
              disabled={busy === 'csv'}
              data-testid="export-csv-btn"
              className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-1.5 rounded font-medium"
            >
              {busy === 'csv' ? '...' : '📊 CSV'}
            </button>
            <button
              onClick={exportPdf}
              disabled={busy === 'pdf'}
              data-testid="export-pdf-btn"
              className="text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded font-medium"
            >
              {busy === 'pdf' ? '...' : '📄 PDF'}
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        {data && (
          <div className="p-3 border-b grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="report-summary">
            {Object.entries(data.summary || {}).map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-200 rounded p-2">
                <p className="text-[10px] uppercase text-gray-500 tracking-wider font-semibold">{k}</p>
                <p className="text-base font-bold text-gray-900">{v}</p>
              </div>
            ))}
          </div>
        )}

        {/* Data table */}
        <div className="flex-1 overflow-auto p-2" data-testid="report-table-container">
          {loading ? (
            <p className="text-center text-gray-500 p-8">Loading...</p>
          ) : data && data.rows.length === 0 ? (
            <p className="text-center text-gray-500 p-8">Is date range me koi data nahi mila.</p>
          ) : data ? (
            <table className="w-full text-xs" data-testid="report-table">
              <thead className="bg-purple-50 sticky top-0 z-10">
                <tr>
                  {data.columns.map((c) => (
                    <th key={c} className="text-left px-2 py-1.5 text-[10px] uppercase font-bold text-purple-900 border-b border-purple-200">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    {row.map((cell, j) => (
                      <td key={j} className="px-2 py-1 text-gray-800 whitespace-nowrap max-w-[200px] truncate" title={String(cell)}>
                        {cell || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-slate-50 flex items-center justify-between text-xs">
          <span className="text-gray-600">{data ? `${data.rows.length} rows` : ''}</span>
          <button onClick={onClose} className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-1.5 rounded">Close</button>
        </div>
      </div>
    </div>
  );
}
